import type { WhiteboardSessionBoard } from "@/lib/whiteboard/boardResults";
import type { SuggestedQuestion, WeakArea } from "@/types/branching-canvas";
import { z } from "zod";

/**
 * The lesson notebook is built only from the tutor's own words and the board's
 * results. Learner speech recognition is never an input: the voice model hears
 * learners better than transcription does, so a misheard turn must not become a
 * phrase or a "mistake".
 */
export const NOTEBOOK_LIMITS = {
  tutorTurns: 6,
  turnChars: 600,
  boardPhrases: 8,
  phraseChars: 160,
  phrases: 40,
  responsePhrases: 8,
  corrections: 3,
  weakAreas: 6,
  suggestions: 3,
};

const languageName = z
  .string()
  .max(40)
  .regex(/^[\p{L}\p{M} ()-]*$/u);

export const NotebookRequestSchema = z
  .object({
    tutorTurns: z.array(z.string().max(4000)).max(20),
    boardPhrases: z.array(z.string().max(1000)).max(20),
    targetLanguage: languageName,
    languageLevel: z.string().max(20),
    noteLanguage: languageName.optional(),
  })
  .strict();

export const NotebookAnalysisSchema = z
  .object({
    phrases: z.array(
      z
        .object({
          text: z.string(),
          /** The phrase's own language; only targetLanguage phrases are kept. */
          language: z.string(),
          translation: z.string(),
          concept: z.string(),
        })
        .strict()
    ),
    corrections: z.array(
      z
        .object({ text: z.string(), quote: z.string(), concept: z.string(), note: z.string() })
        .strict()
    ),
    suggestions: z.array(
      z
        .object({
          text: z.string(),
          category: z.enum(["grammar", "vocabulary", "practice", "explanation"]),
          relatedConcept: z.string().optional(),
        })
        .strict()
    ),
  })
  .strict();
export type NotebookAnalysis = z.infer<typeof NotebookAnalysisSchema>;

export const EMPTY_ANALYSIS: NotebookAnalysis = { phrases: [], corrections: [], suggestions: [] };

export const normalizePhrase = (text: string) =>
  text
    .toLocaleLowerCase()
    .replace(/[\s.,!?;:¡¿"“”«»'’‘()…–—-]+/g, " ")
    .trim();

/**
 * Keep only target-language phrases found, as whole words, in what the tutor
 * said or the board showed. Tutors quote instruction-language prompts ("How do
 * you say 'They eat lunch'?"), so being said is not enough.
 */
export function groundNotebook(
  analysis: NotebookAnalysis,
  sources: { tutorTurns: string[]; boardPhrases: string[]; targetLanguage: string }
): NotebookAnalysis {
  const spoken = ` ${normalizePhrase(sources.tutorTurns.join(" "))} `;
  const board = new Set(sources.boardPhrases.map(normalizePhrase));
  const target = normalizePhrase(sources.targetLanguage);
  const within = (haystack: string, text: string) => {
    const needle = normalizePhrase(text);
    return Boolean(needle) && haystack.includes(` ${needle} `);
  };
  const short = (text: string) => text.trim().length <= NOTEBOOK_LIMITS.phraseChars;
  return {
    phrases: analysis.phrases
      .filter(
        ({ text, language }) =>
          short(text) &&
          Boolean(target) &&
          normalizePhrase(language) === target &&
          (within(spoken, text) || board.has(normalizePhrase(text)))
      )
      .slice(0, NOTEBOOK_LIMITS.responsePhrases),
    corrections: analysis.corrections
      .filter(
        ({ text, quote }) =>
          short(text) && within(spoken, quote) && within(` ${normalizePhrase(quote)} `, text)
      )
      .slice(0, NOTEBOOK_LIMITS.corrections),
    suggestions: analysis.suggestions
      .filter(({ text }) => text.trim() && short(text))
      .slice(0, NOTEBOOK_LIMITS.suggestions),
  };
}

export interface NotebookPhrase {
  key: string;
  text: string;
  translation: string;
  concepts: string[];
  /** Only the tutor's explicit correction or a wrong board answer sets this. */
  needsReview: boolean;
  firstSeenAt: number;
}

export interface LessonNotebookState {
  phrases: NotebookPhrase[];
  /** Concepts behind the tutor's explicit corrections. */
  weakAreas: WeakArea[];
  suggestions: SuggestedQuestion[];
}

export const EMPTY_NOTEBOOK: LessonNotebookState = { phrases: [], weakAreas: [], suggestions: [] };

/** Board phrases (key sentences) for the analysis request, oldest first. */
export function boardPhrases(boards: WhiteboardSessionBoard[]): string[] {
  return [...new Set(boards.map((board) => board.result.label?.trim() ?? ""))]
    .filter((label) => label && label.length <= NOTEBOOK_LIMITS.phraseChars)
    .slice(-NOTEBOOK_LIMITS.boardPhrases);
}

function upsert(
  phrases: Map<string, NotebookPhrase>,
  text: string,
  now: number,
  patch: { translation?: string; concept?: string; needsReview?: boolean }
) {
  const clean = text.trim();
  const key = normalizePhrase(clean);
  if (!key || clean.length > NOTEBOOK_LIMITS.phraseChars) return;
  const known = phrases.get(key);
  const concepts = known?.concepts ?? [];
  const concept = patch.concept?.trim();
  phrases.set(key, {
    key,
    text: known?.text ?? clean,
    translation: patch.translation?.trim() || known?.translation || "",
    concepts:
      concept && !concepts.includes(concept) ? [...concepts, concept].slice(0, 2) : concepts,
    needsReview: Boolean(known?.needsReview || patch.needsReview),
    firstSeenAt: known?.firstSeenAt ?? now,
  });
}

function settle(
  phrases: Map<string, NotebookPhrase>,
  weakAreas: WeakArea[],
  suggestions: SuggestedQuestion[]
): LessonNotebookState {
  return {
    phrases: [...phrases.values()]
      .sort((a, b) => a.firstSeenAt - b.firstSeenAt)
      .slice(-NOTEBOOK_LIMITS.phrases),
    weakAreas: weakAreas.slice(0, NOTEBOOK_LIMITS.weakAreas),
    suggestions: suggestions.slice(0, NOTEBOOK_LIMITS.suggestions),
  };
}

/**
 * Accumulate a tutor-derived analysis: phrases keep their first appearance, a
 * correction marks its correct form for review and counts once per concept.
 */
export function mergeAnalysis(
  previous: LessonNotebookState,
  analysis: NotebookAnalysis,
  now: number
): LessonNotebookState {
  const phrases = new Map(previous.phrases.map((phrase) => [phrase.key, phrase]));
  for (const phrase of analysis.phrases)
    upsert(phrases, phrase.text, now, { translation: phrase.translation, concept: phrase.concept });
  const weak = new Map(previous.weakAreas.map((area) => [normalizePhrase(area.concept), area]));
  for (const correction of analysis.corrections) {
    const concept = correction.concept.trim();
    const known = phrases.get(normalizePhrase(correction.text));
    // The same tutor turn stays in later request windows; count it once.
    const repeat = Boolean(known?.needsReview && (!concept || known.concepts.includes(concept)));
    upsert(phrases, correction.text, now, { concept, needsReview: true });
    if (!concept || repeat) continue;
    const key = normalizePhrase(concept);
    const area = weak.get(key);
    weak.set(key, {
      concept: area?.concept ?? concept,
      frequency: (area?.frequency ?? 0) + 1,
      suggestion: correction.note.trim() || area?.suggestion || "",
    });
  }
  return settle(
    phrases,
    [...weak.values()].sort((a, b) => b.frequency - a.frequency),
    analysis.suggestions.length ? analysis.suggestions : previous.suggestions
  );
}

/** Normalized key sentences of boards the learner tapped wrongly at least once. */
export function missedBoardKeys(boards: WhiteboardSessionBoard[]): Set<string> {
  return new Set(
    boards
      .filter((board) => board.result.incorrect > 0 && board.result.label?.trim())
      .map((board) => normalizePhrase(board.result.label))
  );
}

/**
 * Flag notebook phrases whose board was answered wrongly. A board label can be an
 * instruction-language prompt, so boards never add phrases themselves; phrases
 * arrive only through the language-checked analysis.
 */
export function withBoardReview(
  notebook: LessonNotebookState,
  missed: Set<string>
): LessonNotebookState {
  if (!missed.size) return notebook;
  return {
    ...notebook,
    phrases: notebook.phrases.map((phrase) =>
      !phrase.needsReview && missed.has(phrase.key) ? { ...phrase, needsReview: true } : phrase
    ),
  };
}
